import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { last } from "lodash";

function isOverflown(element) {
  return element.scrollWidth > element.clientWidth;
}

const maxCharactersPerTitleRow = 22;

function getTitlefontSize({ isTitleOverflown, maxWordLength }) {
  if (maxWordLength > maxCharactersPerTitleRow) {
    return 14;
  }
  if (isTitleOverflown) {
    return 16;
  }

  return 18;
}

const minTitleFontSize = 9;
const breakWordsBelowFontSize = 12;
const suitIconClearance = 4;

// Room the title may take: the container's height minus whatever of it the top-left suit icon
// covers. Uses layout offsets rather than getBoundingClientRect so hover scales and flip
// animations on the card do not skew the measurement.
function availableTitleHeight(container) {
  const icon = container.closest(".card")?.querySelector(".card-suit-img-1");
  const iconBottom = icon ? icon.offsetTop + icon.offsetHeight + suitIconClearance : 0;
  return container.clientHeight - iconBottom;
}

// Steps the font size down until the title fits its half of the card. The title is anchored to
// the bottom of its container, so a title that is too tall grows up over the card's top edge.
// `--card-title-max-size` lets a layout with smaller cards (Topik on phones) cap the size.
function fitTitle(titleElement, baseFontSize) {
  const container = titleElement.parentElement;
  if (!container || container.clientHeight === 0) {
    return;
  }

  const maxHeight = availableTitleHeight(container);
  const { style } = titleElement;
  const setSize = (size) => {
    style.fontSize = `min(var(--card-title-max-size, ${size}px), ${size}px)`;
  };
  // Centered text that is too wide spills out on both sides, and scrollWidth only sees the right
  // side, so compare the text's own extent against the element instead.
  const range = document.createRange();
  range.selectNodeContents(titleElement);
  const overflows = () => titleElement.offsetHeight > maxHeight
    || range.getBoundingClientRect().width > titleElement.getBoundingClientRect().width + 0.5;

  // Shrinking stops at a readable size; a word still too wide for the card is broken there
  // instead, and only a title that then still does not fit goes smaller.
  let size = baseFontSize;
  style.overflowWrap = "";
  setSize(size);
  while (overflows() && size > breakWordsBelowFontSize) {
    size -= 1;
    setSize(size);
  }
  if (overflows()) {
    style.overflowWrap = "anywhere";
  }
  while (overflows() && size > minTitleFontSize) {
    size -= 1;
    setSize(size);
  }
}

// CardTitle.js
//
// A simple h1 element in a wrapper that renders the card's title.
// Will use the browser's hyphenation dictionary but for Firefox it is
// quite bad for some Finnish titles.
// As fallback will use overflow-wrap: break-word if the title would
// otherwise overflow. In other cases use the hyphenation function.
//
function CardTitle({ title, color }) {
  const [forceRenderTick, setForceRenderTick] = useState(0);
  const titleRef = useRef(null);
  const visibleTitleRef = useRef(null);

  const onResize = useRef(() => {
    setForceRenderTick(forceRenderTick + 1);
  });

  useEffect(() => {
    window.addEventListener("resize", onResize.current);
    return () => window.removeEventListener("resize", onResize.current);
  });

  const maxWordLength = last(title.split(" ").map((w) => w.length).sort());
  const isTitleOverflown = titleRef.current ? isOverflown(titleRef.current) : false;
  const baseFontSize = getTitlefontSize({ isTitleOverflown, maxWordLength });

  useLayoutEffect(() => {
    if (visibleTitleRef.current) {
      fitTitle(visibleTitleRef.current, baseFontSize);
    }
  });

  // Work Sans may arrive after the first layout and change the title's metrics.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled && visibleTitleRef.current) {
        fitTitle(visibleTitleRef.current, baseFontSize);
      }
    });
    return () => { cancelled = true; };
  }, [title, baseFontSize]);

  return (
    <div className="card-title-container">
      <h1 className="card-title measure-stick" aria-hidden={true} ref={titleRef} style={{
        opacity: 0,
        position: "absolute",
      }}>{ title }</h1>
      <h1 className="card-title visible-title" ref={visibleTitleRef} style={{
        color,
        letterSpacing: isTitleOverflown ? -1.0 : 0,
        fontSize: `min(var(--card-title-max-size, ${baseFontSize}px), ${baseFontSize}px)`,
      }}>
        { title }
      </h1>
    </div>
  );
}

CardTitle.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

export default CardTitle;
